import {
    useContext,
    type FC
} from "react";
import {
    View
} from "react-native";
import type ITracePlanProps from "./type";
import stylesheet, {
    getTracePlanStatus,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useTraceOpen
} from "../../util";
import {
    formatNumber
} from "../../../../helpers/locale";
import {
    TraceContext
} from "../../context";
import TraceCollapsible from "../collapsible";
import Progress from "../../../progress";
import TraceContent from "../content";
import TraceTrigger from "../trigger";
import TraceLine from "../line";

const TracePlan: FC<ITracePlanProps> = ({
    customLocalize: customLocalizeProp,
    customTheme: customThemeProp,
    isOpen: isOpenProp,
    isDefaultOpen,
    onOpenChange,
    onStopPress,
    steps = [],
    style,
    title,
    ...props
}) => {
    const traceContext = useContext(TraceContext);

    const customLocalize = customLocalizeProp ?? traceContext?.customLocalize;

    const customTheme = customThemeProp ?? traceContext?.customTheme;

    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const planStatus = getTracePlanStatus({
        steps
    });

    const isRunning = planStatus === "running";

    const {
        handleOpenChange,
        isOpen
    } = useTraceOpen({
        isAutoOpenWhileRunning: true,
        isOpen: isOpenProp,
        isDefaultOpen,
        onOpenChange,
        isRunning
    });

    const {
        progress: progressDynamicStyle
    } = useStyles({
        shapeConfigs,
        spaces
    });

    const doneCount = steps.filter((step) => step.status === "done").length;

    const description = `${formatNumber({
        locale: activeLocale,
        value: doneCount
    })} / ${formatNumber({
        locale: activeLocale,
        value: steps.length
    })}`;

    const renderProgress = () => {
        if(!steps.length) {
            return null;
        }

        return <Progress
            valueFormatter={(value, max) => localize("x-of-y-steps-completed", [
                formatNumber({
                    locale: activeLocale,
                    value
                }),
                formatNumber({
                    locale: activeLocale,
                    value: max
                })
            ])}
            aria-label={localize("plan-progress")}
            customLocalize={customLocalize}
            style={progressDynamicStyle}
            customTheme={customTheme}
            isValueVisible={false}
            max={steps.length}
            value={doneCount}
            variant="linear"
            size="small"
        />;
    };

    return <TraceCollapsible
        {...props}
        onOpenChange={handleOpenChange}
        customTheme={customTheme}
        isOpen={isOpen}
        style={style}
    >
        <TraceTrigger
            onStopPress={isRunning ? onStopPress : undefined}
            customLocalize={customLocalize}
            customTheme={customTheme}
        >
            <TraceLine
                customTheme={customTheme}
                description={description}
                status={planStatus}
                title={title}
            />
        </TraceTrigger>
        <TraceContent
            customTheme={customTheme}
            isGuideHidden={true}
        >
            {renderProgress()}
            <View
                style={stylesheet.steps}
            >
                {
                    steps.map((step, stepIndex) => <TraceLine
                        isConnected={stepIndex < steps.length - 1}
                        key={`${step.title}-${stepIndex}`}
                        description={step.description}
                        customTheme={customTheme}
                        status={step.status}
                        title={step.title}
                    />)
                }
            </View>
        </TraceContent>
    </TraceCollapsible>;
};
export default TracePlan;
